import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import AxiosInstance from "@/utils/AxiosInstance";
import { readUserSwitchSessionData, UserSwitchSessionDataType } from "@/types/linkedProfile";
import * as Sentry from "@sentry/react";

type Role =
  | "admin"
  | "manager"
  | "staff"
  | "coordinator"
  | "provider"
  | "patient"
  | "caretaker"
  | 'practitioner'
  | "portal_access";

/** Sidebar still uses Permission.json-like keys (`isAuthorized`). API sends `canAccess*`. Map legacy → API. */
const LEGACY_PERMISSION_TO_CAN_ACCESS: Record<string, string> = {
  canViewOrganizationsInSidebar: "canAccessOrganizations",
  canViewProviderInSidebar: "canAccessClinicians",
  canViewCaretakerInSidebar: "canAccessCaretakers",
};

/** Admin-style actions in a module → `canAccess*` flag (tenant managers). */
const LEGACY_ACTION_TO_CAN_ACCESS: Record<string, string> = {
  ...LEGACY_PERMISSION_TO_CAN_ACCESS,
  canAddOrganizations: "canAccessOrganizations",
  canViewCaretakerManagement: "canAccessCaretakers",
  canViewDetailsOfCaretakers: "canAccessCaretakers",
  canAddPatients: "canAccessPatients",
  canAddNursingNotes: "canAccessPatients",
  CanAddNursingNotes: "canAccessPatients",
  canViewInactivePatients: "canAccessPatients",
  canAddViewProviders: "canAccessClinicians",
  canAddPractitioners: "canAccessClinicians",
};

export type ManagerScope = "tenant" | "provider";

function resolveCanAccessModule(permission: string): string | undefined {
  if (permission.startsWith("canAccess")) return permission;
  return LEGACY_ACTION_TO_CAN_ACCESS[permission];
}

/** Admin-created manager (`managerScope: "tenant"`). */
export function isTenantManager(user: User | undefined): boolean {
  if (!user || user.role !== "manager") return false;
  const scope = String(user.managerScope ?? "")
    .trim()
    .toLowerCase();
  return scope === "tenant";
}

/** API may send booleans or strings (`"true"` / `"false"`). */
export function permissionFlagTruthy(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value === null || value === undefined) return false;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no" || s === "") return false;
  }
  return Boolean(value);
}

export type OrganizationAssignment =
  | { scope: "all" }
  | { scope: "selected"; organizationIds: string[] };

type User =
  | {
      id: string;
      role?: Role;
      managerScope?: ManagerScope | string;
      caretakerId?: string | null;
      providerId?: string | null;
      name?: string;
      email?: string;
      firstName?: string;
      lastName?: string;
      phone?: string;
      organizationAssignment?: OrganizationAssignment;
    }
  | undefined;

const PERMISSION_NAV_ROLES = new Set<Role>(["manager", "staff", "coordinator"]);

export function isPermissionNavRole(role: string | undefined): boolean {
  return PERMISSION_NAV_ROLES.has(role as Role);
}

export function filterOrganizationsForUser(
  orgs: any[],
  user: User,
): any[] {
  if (!Array.isArray(orgs) || !user) return orgs ?? [];
  const assignment = user.organizationAssignment;
  if (assignment?.scope === "selected" && assignment.organizationIds?.length) {
    const allowed = new Set(assignment.organizationIds.map(String));
    return orgs.filter((o) => {
      const orgId = String(o?._id ?? o?.id ?? "");
      return orgId && allowed.has(orgId);
    });
  }
  return orgs;
}

function authPayloadBlock(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== "object") return {};
  const root = raw as Record<string, unknown>;
  const inner = root.data;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return root;
}

export function normalizeAuthPayload(raw: unknown) {
  const block = authPayloadBlock(raw);
  const userRaw = block.user;
  const userRecord =
    userRaw && typeof userRaw === "object"
      ? ({ ...(userRaw as Record<string, unknown>) } as Record<string, unknown>)
      : undefined;

  let permissions: Record<string, unknown> = {};
  if (block.permissions && typeof block.permissions === "object") {
    permissions = block.permissions as Record<string, unknown>;
  } else if (
    userRecord?.permissions &&
    typeof userRecord.permissions === "object"
  ) {
    permissions = userRecord.permissions as Record<string, unknown>;
  }

  const { permissions: _ignored, ...userFields } = userRecord ?? {};
  const organizationAssignment =
    userFields.organizationAssignment ?? block.organizationAssignment;
  const normalizedUser = userRecord
    ? ({
        ...userFields,
        role: userFields.role ?? block.role,
        organizationAssignment,
      } as User)
    : undefined;

  return {
    user: normalizedUser,
    token: block.token,
    permissions,
    profile: block.profile,
    linkedProfiles: block.linkedProfiles,
  };
}

export function userHasOrgAssignmentAll(user: User): boolean {
  const scope = user?.organizationAssignment?.scope;
  return scope === "all" || user?.role === "admin";
}

type UserContextType = {
  isLoading: boolean;
  isAuthenticated: boolean;
  user: User;
  profile?: any;
  setProfile: (data:any)=> void;
  displayName: string;
  patientEmail: string | null;
  caretaker?: any;
  provider?: any;
  /** Patients only: true when `/patients/with-legacy-data` returns rows. */
  patientHasLegacyData: boolean;
  signIn: (user: User | any, token: any, permissions: any, profile?: any, linkedProfiles?: UserSwitchSessionDataType) => Promise<void>;
  signOut: (cb : any) => Promise<void>;
  setUser: (data:any)=> void;
  userSwitchSessionData: UserSwitchSessionDataType | null;
  setUserSwitchSessionData: (data: UserSwitchSessionDataType | null) => void;
  isAuthorized: (permission: string) => boolean;
  /** Navigation flags from login/`/auth/me` (`canAccessPatients`, …). Admins bypass (full access). */
  canAccess: (apiPermissionKey: string) => boolean;
  /** Admin, or tenant manager with the matching `canAccess*` module. */
  hasModuleAdminAccess: (moduleOrLegacyKey: string) => boolean;
  isTenantManager: boolean;
  // organizations: any[];
  // setOrganizations: (orgs: any[]) => void;
  // selectedOrgId: string | null;
  // setSelectedOrgId: (orgId: string | null) => void;
};

// 1️⃣ Create Context
const UserContext = createContext<UserContextType | undefined>(undefined);

// 2️⃣ Provider Component
export const UserContextProvider = ({ children }: { children: ReactNode }) => {
  // console.log("Running UserContextProvider");

  // User switch session data is used to store the data of the user switch session. + after getME() also store data in this.

  const [userSwitchSessionData, setUserSwitchSessionData] = useState<UserSwitchSessionDataType | null>(readUserSwitchSessionData() || null);
  const [user, setUser] = useState<User>(undefined);
  const [token, setToken] = useState<any>(
    localStorage.getItem("userdatatoken") || null,
  );

  const [isLoading, setIsLoading] = useState(true);

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // const [patientEmail, setPatientEmail] = useState<string | null>(null);
  const [permissionList, setPermissionList] = useState<any>({});
  const [profile, setProfile] = useState<any>(null);
  const [patientHasLegacyData, setPatientHasLegacyData] = useState(false);

  let patientEmail =
    user && user.role === "patient" ? user.email || null : null;

  // Full name from profile (firstName + middleName + lastName) when available, else user.name
  const displayName =
    profile && (profile.firstName != null || profile.lastName != null)
      ? [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ").trim()
      : user?.name ?? "";

  const patientProfileId = String(profile?._id ?? profile?.id ?? "").trim();

  useEffect(() => {
    async function getMe() {
      try {
        setIsLoading(true);
        const { data } = await AxiosInstance().get<unknown>("/auth/me");
        const session = normalizeAuthPayload(data);
        if (!session.user) {
          throw new Error("User not found in /auth/me response");
        }
        await signIn(
          session.user,
          token,
          session.permissions,
          session.profile,
          session.linkedProfiles ?? [],
        );
      } catch (error) {
        signOut(()=> {});
      } finally {
        setIsLoading(false);
      }
    }
    //  adding this as we not getting profile from in signIn so we calling it again...
   if( token && (!user || !profile)) {  
    getMe();
   }
   else{
    setIsLoading(false);
   }
  }, [token]);

  // Patients only: prefetch legacy data so Past Medical Records nav can be gated.
  useEffect(() => {
    if (user?.role !== "patient" || !patientProfileId) {
      setPatientHasLegacyData(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await AxiosInstance().get<{
          data?: { patients?: unknown[] };
        }>("/patients/with-legacy-data", {
          params: {
            search: "",
            patientId: patientProfileId,
            refreshKey: 0,
            page: 1,
            limit: 10,
            skip: 0,
          },
        });
        if (cancelled) return;
        const patients = result?.data?.data?.patients;
        setPatientHasLegacyData(
          Array.isArray(patients) && patients.length > 0,
        );
      } catch {
        if (!cancelled) setPatientHasLegacyData(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.role, patientProfileId]);

  async function signIn(user?: User, token?: any, permissions?: any, profile?: any,linkedProfiles?: any) {
    console.log({ user, permissions, profile });
    if (!user || !token) {
      throw new Error("All fields are not availble");
    }

    localStorage.setItem("userdatatoken", token);

    // id + role only — never email/name/phone, so no PII reaches Sentry.
    Sentry.setUser({ id: user.id, role: user.role ?? "unknown" });

    setToken(token);
    setUser(user);
    setPermissionList(
      permissions && typeof permissions === "object" ? permissions : {},
    );
    setUserSwitchSessionData({ token, linkedProfiles });
    setProfile(profile || null);
    if (user?.role !== "patient") {
      setPatientHasLegacyData(false);
    }
    setIsAuthenticated(true);
    setIsLoading(false);
  }

  async function signOut(cb :any) {
    cb && cb()
    // Preserve Anthro AI IdToken across signOut so the AI chat
    // doesn't immediately lose its static Cognito token.
    const anthroAiIdToken = localStorage.getItem("anthroAiIdToken");
    localStorage.clear();
    if (anthroAiIdToken) {
      localStorage.setItem("anthroAiIdToken", anthroAiIdToken);
    }

    Sentry.setUser(null);

    setUser(undefined);
    setProfile(null);
    setPatientHasLegacyData(false);
    setIsAuthenticated(false);

    // window.location.pathname = "/auth";
  }
  function moduleFlagEnabled(moduleKey: string) {
    return permissionFlagTruthy(permissionList?.[moduleKey]);
  }

  function isAuthorized(permission: string) {
    if (!user || !user?.role) return false;
    if (permission === "adminOnly") return user?.role === "admin";
    if (permission === "isPatient") return user?.role === "patient";
    if (user?.role === "admin") return true;

    if (isTenantManager(user)) {
      const moduleKey = resolveCanAccessModule(permission);
      if (moduleKey && moduleFlagEnabled(moduleKey)) return true;
    }

    if (permissionFlagTruthy(permissionList?.[permission])) return true;
    const mapped = LEGACY_PERMISSION_TO_CAN_ACCESS[permission];
    if (mapped && moduleFlagEnabled(mapped)) return true;
    return false;
  }

  function canAccess(apiPermissionKey: string) {
    if (!user?.role) return false;
    if (user.role === "admin") return true;
    const v = permissionList?.[apiPermissionKey];
    // console.log(apiPermissionKey,v);
    if (v !== undefined && v !== null) return permissionFlagTruthy(v);
    // Caretakers may not get `canAccess*` on older APIs; keep Engage + Patients available.
    if (user.role === "caretaker" && apiPermissionKey === "canAccessEngage") return true;
    if (user.role === "caretaker" && apiPermissionKey === "canAccessPatients") return true;
    return false;
  }

  function hasModuleAdminAccess(moduleOrLegacyKey: string) {
    if (!user?.role) return false;
    if (user.role === "admin") return true;
    if (!isTenantManager(user)) return false;
    const moduleKey = resolveCanAccessModule(moduleOrLegacyKey);
    return moduleKey ? moduleFlagEnabled(moduleKey) : false;
  }

  const tenantManager = isTenantManager(user);

  const value: UserContextType = {
    isLoading,
    isAuthenticated,
    user,
    profile,
    setProfile,
    displayName: displayName || user?.name || "",
    patientEmail,
    caretaker: undefined,
    provider: undefined,
    patientHasLegacyData,
    signIn,
    signOut,
    setUser,
    userSwitchSessionData,
    setUserSwitchSessionData,
    isAuthorized,
    canAccess,
    hasModuleAdminAccess,
    isTenantManager: tenantManager,
    // organizations, setOrganizations,
    // selectedOrgId, setSelectedOrgId
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// 3️⃣ Hook to consume context
export function useAuth() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useAuth must be used within a UserContextProvider");
  }
  return context;
}
